import React, { useCallback, useMemo, useState } from "react";
import { SignalType } from "@types";
import { useAppDispatch, useAppSelector } from "@store";
import { updateSignalsFilters } from "@store/signals";
import { Button, ButtonSize, ButtonVariant, Scrollbar } from "@taskpane/components";
import { FilterSelectDropdown } from "./components";
import css from "./FiltersSelectContent.module.scss";

export interface FilterSelectContentProps {
  handleChange: (value: unknown, shouldClose: boolean) => void;
}

const FilterSelectContent: React.FC<FilterSelectContentProps> = ({ handleChange: onChange }) => {
  const dispatch = useAppDispatch();

  const eventNames = useAppSelector((state) => state.signals.filters.eventName);

  const eventNamesSet = useMemo(() => new Set(eventNames), [eventNames]);

  const [filterOptions, setFilterOptions] = useState(() => [
    {
      label: "Email Opens",
      id: "emailOpens",
      options: [
        {
          label: "Opened",
          value: SignalType.EmailOpened,
          isSelected: eventNamesSet.has(SignalType.EmailOpened),
        },
        // { label: "Opened but Not Replied", value: "emailOpenedNotReplied", isSelected: false },
      ],
    },
    {
      label: "Sentiment Shifts",
      id: "sentimentShifts",
      options: [
        {
          label: "Positive Sentiment",
          value: SignalType.SentimentIncreased,
          isSelected: eventNamesSet.has(SignalType.SentimentIncreased),
        },
        {
          label: "Negative Sentiment",
          value: SignalType.SentimentDecreased,
          isSelected: eventNamesSet.has(SignalType.SentimentDecreased),
        },
      ],
    },
    {
      label: "Carbon Dynamics",
      id: "carbonDynamics",
      options: [
        {
          label: "Someone was Added",
          value: SignalType.ToAdded,
          isSelected: eventNamesSet.has(SignalType.ToAdded),
        },
        {
          label: "Someone was Removed",
          value: SignalType.ToRemoved,
          isSelected: eventNamesSet.has(SignalType.ToRemoved),
        },
        {
          label: "Someone was CC'ed",
          value: SignalType.CcAdded,
          isSelected: eventNamesSet.has(SignalType.CcAdded),
        },
        {
          label: "Someone was Removed from CC",
          value: SignalType.CcRemoved,
          isSelected: eventNamesSet.has(SignalType.CcRemoved),
        },
        {
          label: "Someone may have Added a Third Party",
          value: SignalType.ToThirdPartyAdded,
          isSelected: eventNamesSet.has(SignalType.ToThirdPartyAdded),
        },
        {
          label: "A Third Party was Remove",
          value: SignalType.ToThirdPartyRemoved,
          isSelected: eventNamesSet.has(SignalType.ToThirdPartyRemoved),
        },
        {
          label: "Point of Contact may have Changed",
          value: SignalType.PointOfContactChanged,
          isSelected: eventNamesSet.has(SignalType.PointOfContactChanged),
        },
      ],
    },
  ]);

  const selectedEventNames = useMemo(
    () =>
      filterOptions
        .flatMap((group) => group.options)
        .filter(({ isSelected }) => isSelected)
        .map(({ value }) => value),
    [filterOptions]
  );

  const isSelectedFiltersUnsaved = useMemo(
    () =>
      selectedEventNames.length !== eventNames.length ||
      selectedEventNames.some((eventName) => !eventNamesSet.has(eventName)),
    [eventNames, eventNamesSet, selectedEventNames]
  );

  const handleChange = useCallback((id: string, isSelected: boolean) => {
    setFilterOptions((prevState) =>
      prevState.map((group) =>
        group.id === id
          ? {
              ...group,
              options: group.options.map((option) => ({ ...option, isSelected })),
            }
          : group
      )
    );
  }, []);

  const handleItemChange = useCallback((value: string) => {
    setFilterOptions((prevState) =>
      prevState.map((group) => {
        const newOptions = group.options.map((option) =>
          option.value === value ? { ...option, isSelected: !option.isSelected } : option
        );
        return { ...group, options: newOptions };
      })
    );
  }, []);

  const attrs = {
    container: {
      className: css.container,
    },
    scrollbar: {
      className: css.scrollbar,
    },
    optionsWrapper: {
      className: css.optionsWrapper,
    },
    buttonsWrapper: {
      className: css.buttonsWrapper,
    },
    clearButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Secondary,
      disabled: !selectedEventNames.length,
      onClick: () => {
        setFilterOptions((prevState) =>
          prevState.map((group) => ({
            ...group,
            options: group.options.map((option) => ({ ...option, isSelected: false })),
          }))
        );
      },
    },
    applyButton: {
      size: ButtonSize.XS,
      variant: ButtonVariant.Default,
      disabled: !isSelectedFiltersUnsaved,
      onClick: () => {
        if (isSelectedFiltersUnsaved) {
          onChange(undefined, true);
          dispatch(
            updateSignalsFilters({
              eventName: selectedEventNames,
            })
          );
        }
      },
    },
  };

  const renderOptions = () =>
    filterOptions.map(({ id, label, options }) => {
      const attrs = {
        filterSelectDropdown: {
          id,
          label,
          options,
          onChange: handleChange,
          onItemChange: handleItemChange,
        },
      };

      return <FilterSelectDropdown {...attrs.filterSelectDropdown} key={id} />;
    });

  return (
    <div {...attrs.container}>
      <Scrollbar {...attrs.scrollbar}>
        <div {...attrs.optionsWrapper}>{renderOptions()}</div>
      </Scrollbar>
      <div {...attrs.buttonsWrapper}>
        <Button {...attrs.clearButton}>
          <span>Clear</span>
        </Button>
        <Button {...attrs.applyButton}>
          <span>Apply</span>
        </Button>
      </div>
    </div>
  );
};

export default FilterSelectContent;
